<template>
  <div class="color-schemes">
    <button
      v-for="scheme of colorSchemes"
      :key="scheme.id"
      v-tooltip="{
        content: scheme.label,
        ...tooltipOptionsDefaults,
        placement: 'top',
      }"
      type="button"
      class="color-schemes__card"
      :class="{
        'color-schemes__card--active': isSchemeActive(scheme),
      }"
      :aria-pressed="isSchemeActive(scheme) ? 'true' : 'false'"
      @click="applyColorScheme(scheme.id)"
    >
      <div class="color-schemes__swatches">
        <span
          v-for="color of scheme.colors"
          :key="color"
          class="color-schemes__swatch"
          :style="{ backgroundColor: color }"
        ></span>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useFormStore, type ColorScheme } from '@/stores/useFormStore';
  import { tooltipOptionsDefaults } from '@/helpers/tooltipOptionsDefaults';

  const formStore = useFormStore();
  const { applyColorScheme, getColorHexById } = formStore;
  const { colorSchemes, selectedThemeColorIds } = storeToRefs(formStore);

  const selectedThemeColorHexes = computed(() => selectedThemeColorIds.value
    .map((id) => getColorHexById(id)));

  function strictEqual (
    left: Array<string | null>,
    right: Array<string | null>,
  ) {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((colorHex, index) => colorHex === right[index]);
  }

  function isSchemeActive (scheme: ColorScheme) {
    return strictEqual(scheme.colors, selectedThemeColorHexes.value);
  }
</script>

<style lang="scss">
  .color-schemes {
    display: flex;
    flex-flow: row wrap;
    gap: 12px;
  }

  button.color-schemes__card {
    flex: 1 0 90px;
    max-width: 130px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1;
    padding: 0;
    border-radius: 14px;
    background: transparent !important;
    box-shadow: inset 0 0 0 1px color-opacity(--color-black, 7%);
    transition: box-shadow var(--speed-fast) var(--easing-default), transform var(--speed-fast) var(--easing-default);
    cursor: pointer;
    color: inherit;
    outline: none !important;
    border: none;
    container-type: size;

    &:hover,
    &:focus,
    &--active {
      box-shadow: inset 0 0 0 2px var(--color-primary);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .color-schemes__swatches {
    display: flex;
    flex-direction: column;
    gap: 6cqh;
    width: 100%;
    padding: 10cqh 16cqw;
  }

  .color-schemes__swatch {
    margin: 0 auto;
    height: 12cqh;
    width: auto;
    aspect-ratio: 5;
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px color-opacity(--color-black, 10%);
  }
</style>
